import {BP_MD} from '../../elements/carousel/carousel.breakpoints.js';
import {
    buildExternalSyncCarousel,
    CSS_SLIDE_GAP_FALLBACK,
    CSS_VAR_SYNC_INSET,
    CSS_VAR_SYNC_WIDTH,
} from '../../elements/carousel/carousel.presets.sync.util.js';

/** @typedef {import('../../elements/carousel/carousel.presets.util.js').CarouselBuildContext} CarouselBuildContext */

const TESTIMONIALS_EXTERNAL_SYNC = {
    swiperSelector: '.testimonials__swiper',
    textBlockSelector: '.testimonials__slide-text',
    nextButtonSelector: '.testimonials__btn-next',
    sectionSelector: '.testimonials',
    alignAnchorSelector: '.textLockUp__body',
    activeTextClass: 'is-active',
    slideGapVar: CSS_SLIDE_GAP_FALLBACK,
    metaWidthVar: CSS_VAR_SYNC_WIDTH,
    metaInsetVar: CSS_VAR_SYNC_INSET,
    autoplayDisabledClass: 'testimonials--autoplay-disabled',
};

const DESKTOP_MQ = `(min-width: ${BP_MD}px)`;

function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function readCssLengthPx(element, propertyName, fallback) {
    const value = window.getComputedStyle(element).getPropertyValue(propertyName).trim();
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * Full-bleed portrait strip (former about-team). Shared default for carousel-grid.
 *
 * Meta copy sits under a fixed focal slot (CSS). JS only swaps which quote is
 * active — never remeasures left/width (that caused left↔right jumps on slow 3G
 * while CSS/images/Swiper settled).
 * @param {CarouselBuildContext} ctx
 */
function buildPortraitStripCarousel(ctx) {
    const {$host} = ctx;
    const textBlocks = $host.querySelectorAll('.testimonials__slide-text');
    const nextButton = $host.querySelector('.testimonials__btn-next');
    const slideCount = parseInt($host.dataset.slideCount, 10) || textBlocks.length;
    const autoplayEnabled = $host.dataset.autoplay === 'true' && !prefersReducedMotion();
    let autoplayTimer = null;

    function isDesktop() {
        return window.matchMedia(DESKTOP_MQ).matches;
    }

    function getFocalOffset() {
        return isDesktop() ? 2 : 0;
    }

    /**
     * Active quote = person in the fixed focal slot.
     * Uses Swiper index math only (no layout measurement).
     * @param {import('swiper').Swiper} swiper
     */
    function setActiveText(swiper) {
        const logicalIndex = swiper.activeIndex % slideCount;
        const activeIndex = (logicalIndex + getFocalOffset()) % slideCount;
        textBlocks.forEach((element, index) => {
            element.classList.toggle('is-active', index === activeIndex);
        });
    }

    function applyLayout(swiper) {
        const gap = readCssLengthPx($host, '--carousel-slide-gap', 30);
        const slideWidth = readCssLengthPx(
            $host,
            '--testimonials-card-width',
            (window.innerWidth - gap * 3) / 3,
        );
        const desktop = isDesktop();

        swiper.params.spaceBetween = gap;
        swiper.params.slidesOffsetBefore = desktop ? -(slideWidth / 2) : 8;
        swiper.updateSlides();
        setActiveText(swiper);
    }

    function resetIfCloneCycle(swiper) {
        if (swiper.activeIndex < slideCount) return;

        swiper.slideTo(swiper.activeIndex - slideCount, 0, false);
        setActiveText(swiper);
    }

    function goNext(swiper) {
        if (swiper.animating) return;
        swiper.slideTo(swiper.activeIndex + 1, 400);
    }

    function startAutoplay(swiper) {
        if (!autoplayEnabled || autoplayTimer) return;
        autoplayTimer = window.setInterval(() => {
            goNext(swiper);
        }, 5000);
    }

    return {
        swiperEl: $host.querySelector('.testimonials__swiper'),
        swiperOptions: {
            loop: false,
            watchOverflow: false,
            watchSlidesProgress: true,
            slidesPerGroup: 1,
            slidesPerView: 'auto',
            spaceBetween: 20,
            slidesOffsetBefore: 0,
            speed: 400,
            allowTouchMove: true,
            on: {
                init(swiper) {
                    requestAnimationFrame(() => {
                        applyLayout(swiper);
                        swiper.slideTo(0, 0, false);
                        setActiveText(swiper);
                        nextButton?.addEventListener('click', () => goNext(swiper));
                        startAutoplay(swiper);
                    });
                },
                slideChangeTransitionEnd(swiper) {
                    resetIfCloneCycle(swiper);
                    setActiveText(swiper);
                },
                resize: applyLayout,
                destroy() {
                    if (autoplayTimer) {
                        window.clearInterval(autoplayTimer);
                        autoplayTimer = null;
                    }
                },
            },
        },
    };
}

/** @type {Record<string, (ctx: CarouselBuildContext) => import('../../elements/carousel/carousel.presets.util.js').CarouselBuildResult>} */
export const testimonialsPresets = {
    // Default carousel-grid look (formerly about-team)
    'testimonials-peek-grid': buildPortraitStripCarousel,
    'testimonials-centered': (ctx) =>
        buildExternalSyncCarousel(ctx, {
            ...TESTIMONIALS_EXTERNAL_SYNC,
            layout: 'centered',
        }),
};
